/**
 * The event→animation vocabulary — a declarative table mapping backend event
 * categories to canvas effects. This is the extension point: to make the canvas
 * react to a new event, add a row here. Dispatch never changes.
 *
 * v1 is intentionally coarse — agent status passthrough and routed-message
 * envelopes. Future rows (tool sparks, cost pulses, HITL bubbles) will need new
 * `VisualizationTarget` verbs and matching canvas effects; the sprites and
 * `EffectEntity` already exist for them.
 */

import type { AgentStatus } from "../state/PixelWorldStore.js";
import type { AnimationRegistry } from "./types.js";

export const ANIMATION_REGISTRY: AnimationRegistry = {
  "agent.status-changed": (e, target) => {
    if (e.agentId) target.setAgentStatus(e.agentId, e.status as AgentStatus);
  },

  "agent.spawned": (e, target) => {
    if (e.agentId) target.setAgentStatus(e.agentId, "idle");
  },

  // agent.stopped fires for every agent at the end of every run (instance
  // teardown), not only on real termination. In a persistent living-scene the
  // agent should return to rest — mapping to "terminated" dims the sprite and
  // makes agents appear to vanish after each run.
  "agent.stopped": (e, target) => {
    if (e.agentId) target.setAgentStatus(e.agentId, "idle");
  },

  "agent.output": (e, target) => {
    if (e.agentId) target.setAgentStatus(e.agentId, "running");
  },

  "run.failed": (e, target) => {
    if (e.agentId) target.setAgentStatus(e.agentId, "error");
  },

  "routing.delivered": (e, target, ctx) => {
    const message = e.message as { from?: string; to?: string } | undefined;
    if (!message?.from || !message?.to) return;
    target.enqueueMessage({
      fromAgentId: message.from,
      toAgentId: message.to,
      edgeType: ctx.resolveEdgeType(message.from, message.to),
    });
  },

  "routing.failed": (e, target) => {
    const to = e.to as string | undefined;
    if (to) target.setAgentStatus(to, "error");
  },
};
